import { getContentType, normalizePost } from './contentTypeUtils.js'
import {
  buildPublishChecklist,
  getDeliveryReceiptBadge,
  getLatestPerformanceFeedbackCheckIn,
  hasPerformanceFeedback,
  isPublishChecklistComplete,
  normalizePerformanceFeedback,
} from './studioWorkflowUtils.js'

export function getIntegrationForPublishingMode(
  integrationHealth,
  publishingMode
) {
  if (publishingMode === 'linkedin-post-api') {
    return integrationHealth?.integrations?.['linkedin-post-publishing'] || null
  }
  if (publishingMode === 'x-post-api') {
    return integrationHealth?.integrations?.['x-post-publishing'] || null
  }
  return null
}

export function isLivePublishingMode(publishingMode) {
  return (
    publishingMode === 'linkedin-post-api' || publishingMode === 'x-post-api'
  )
}

export function getPublishReadiness({
  type,
  contentType,
  post = null,
  integrationHealth = null,
} = {}) {
  const resolvedType = type || getContentType(contentType || post?.contentType)
  const mode = resolvedType.publishingMode
  const liveSupported = isLivePublishingMode(mode)

  if (!liveSupported) {
    if (mode === 'export-markdown') {
      return {
        liveSupported: false,
        disabled: true,
        reasonCode: 'MANUAL_EXPORT_ONLY',
        label: 'Export / manual delivery',
        helperText:
          'This output is a durable draft. Export it or record manual delivery after publishing outside the studio.',
      }
    }
    if (mode === 'manual-upload') {
      return {
        liveSupported: false,
        disabled: true,
        reasonCode: 'MANUAL_UPLOAD_ONLY',
        label: 'Manual upload',
        helperText:
          'This output is prepared in the studio, then uploaded manually outside the live API publish flow.',
      }
    }
    return {
      liveSupported: false,
      disabled: true,
      reasonCode: 'UNSUPPORTED_PUBLISHING_MODE',
      label: 'Unsupported live publish',
      helperText: 'No reliable live publishing path exists for this output.',
    }
  }

  if (post?.status === 'scheduled') {
    return {
      liveSupported: true,
      disabled: true,
      reasonCode: 'SCHEDULED_QUEUE',
      label: 'Scheduled',
      helperText:
        'Scheduled drafts stay on the queue. Eligible LinkedIn and X posts become durable publish jobs when autonomy is enabled. Manual formats still require operator delivery.',
    }
  }

  const integration = getIntegrationForPublishingMode(integrationHealth, mode)
  if (!integration) {
    return {
      liveSupported: true,
      disabled: true,
      reasonCode: 'INTEGRATION_STATE_UNKNOWN',
      label: 'Configuration unknown',
      helperText:
        'Runtime integration state is unavailable, so live publishing is disabled here.',
    }
  }
  if (!integration.enabled) {
    return {
      liveSupported: true,
      disabled: true,
      reasonCode: 'LIVE_POSTING_DISABLED',
      label: 'Live posting disabled',
      helperText:
        'ENABLE_LIVE_POSTING is off. Copy the draft or record manual delivery instead.',
    }
  }
  if (!integration.available) {
    return {
      liveSupported: true,
      disabled: true,
      reasonCode: 'CONFIGURATION_REQUIRED',
      label: 'Configuration required',
      helperText:
        'Add valid Settings credentials or environment fallback credentials before using the live API path.',
    }
  }

  return {
    liveSupported: true,
    disabled: false,
    reasonCode: 'READY',
    label: mode === 'x-post-api' ? 'Post to X' : 'Post to LinkedIn',
    helperText:
      mode === 'x-post-api'
        ? 'Supported live API path for X posts and numbered threads.'
        : 'Supported live API path for LinkedIn posts.',
  }
}

export function getLatestPublishAttempt(publishAttempts = [], postId) {
  return publishAttempts.find((attempt) => attempt.postId === postId) || null
}

function createBadge(key, label, tone = 'neutral') {
  return { key, label, tone }
}

function getScheduleState(scheduledFor) {
  if (!scheduledFor) return null
  const scheduled = new Date(scheduledFor)
  if (Number.isNaN(scheduled.getTime())) return null
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)
  const tomorrowStart = new Date(todayStart)
  tomorrowStart.setDate(tomorrowStart.getDate() + 1)
  if (scheduled < todayStart) return 'overdue'
  if (scheduled >= todayStart && scheduled < tomorrowStart) return 'today'
  return 'future'
}

export function derivePostTruthState({
  post,
  publishAttempts = [],
  integrationHealth = null,
}) {
  const normalized = normalizePost(post || {})
  const type = getContentType(normalized.contentType)
  const latestAttempt = getLatestPublishAttempt(publishAttempts, normalized.id)
  const notionIntegration =
    integrationHealth?.integrations?.['notion-post-sync'] || null
  const publishIntegration = getIntegrationForPublishingMode(
    integrationHealth,
    type.publishingMode
  )
  const syncMeta =
    normalized.metadata && typeof normalized.metadata === 'object'
      ? normalized.metadata
      : {}
  const publishChecklist = buildPublishChecklist(
    normalized.contentType,
    syncMeta.publishChecklist
  )
  const hasIncompleteChecklist =
    normalized.status === 'scheduled' &&
    !isPublishChecklistComplete(publishChecklist)
  const deliveryReceipt =
    syncMeta.deliveryReceipt && typeof syncMeta.deliveryReceipt === 'object'
      ? syncMeta.deliveryReceipt
      : null
  const deliveryReceiptBadge = getDeliveryReceiptBadge(deliveryReceipt)
  const performanceFeedback = normalizePerformanceFeedback(
    syncMeta.performanceFeedback
  )
  const latestPerformanceFeedback =
    getLatestPerformanceFeedbackCheckIn(performanceFeedback)
  const hasPerformanceFeedbackCheckIn =
    hasPerformanceFeedback(performanceFeedback)
  const scheduleState = getScheduleState(normalized.scheduledFor)
  const deliveredViaLiveApi =
    latestAttempt?.outcome === 'success' ||
    deliveryReceipt?.method === 'live-api'
  const statusBadge = (() => {
    if (normalized.status === 'published') {
      return createBadge('published', 'Delivered', 'success')
    }
    if (normalized.status === 'scheduled') {
      return createBadge('scheduled', 'Scheduled', 'planning')
    }
    if (
      type.publishingMode === 'export-markdown' ||
      type.publishingMode === 'manual-upload' ||
      type.publishingMode === 'manual-only'
    ) {
      return createBadge('manual', 'Manual / export', 'manual')
    }
    if (
      type.publishingStability === 'quarantined' ||
      type.publishingStability === 'unsupported'
    ) {
      return createBadge('quarantined', 'Quarantined', 'quarantined')
    }
    return createBadge('saved', 'Draft', 'neutral')
  })()

  const badges = [statusBadge]

  if (scheduleState === 'overdue') {
    badges.push(createBadge('overdue-planned', 'Overdue planned item', 'error'))
  } else if (scheduleState === 'today') {
    badges.push(createBadge('due-today', 'Due today', 'planning'))
  }

  if (hasIncompleteChecklist) {
    badges.push(
      createBadge(
        'checklist-incomplete',
        'Checklist incomplete',
        'experimental'
      )
    )
  }

  if (deliveryReceiptBadge) {
    badges.push(
      createBadge(
        'delivery-receipt',
        deliveryReceiptBadge,
        deliveryReceipt?.method === 'live-api' ? 'success' : 'manual'
      )
    )
  }

  if (type.publishingStability === 'experimental') {
    badges.push(
      createBadge('experimental-path', 'Experimental', 'experimental')
    )
  }

  if (latestAttempt?.outcome === 'success') {
    badges.push(createBadge('delivered', 'Live API published', 'success'))
  }

  if (latestAttempt?.outcome === 'failure') {
    badges.push(createBadge('publish-failed', 'Publish failed', 'error'))
  }

  if (type.supportsNotionSync) {
    if (normalized.notionPageId) {
      badges.push(createBadge('synced', 'Synced to Notion', 'success'))
    } else if (syncMeta.lastSyncStatus === 'failure') {
      badges.push(createBadge('sync-failed', 'Sync failed', 'error'))
    } else if (!notionIntegration?.enabled || !notionIntegration?.available) {
      badges.push(createBadge('sync-unavailable', 'Sync unavailable', 'muted'))
    }
  }

  const lastError =
    latestAttempt?.outcome === 'failure'
      ? {
          source: 'publish',
          code: latestAttempt.errorCode,
          message: latestAttempt.errorMessage,
          at: latestAttempt.updatedAt || latestAttempt.createdAt || null,
        }
      : syncMeta.lastSyncStatus === 'failure'
        ? {
            source: 'sync',
            code: syncMeta.lastSyncErrorCode || null,
            message: syncMeta.lastSyncErrorMessage || null,
            at: syncMeta.lastSyncAttemptAt || null,
          }
        : null

  return {
    type,
    latestAttempt,
    badges,
    publishIntegration,
    publishReadiness: getPublishReadiness({
      type,
      post: normalized,
      integrationHealth,
    }),
    notionIntegration,
    canRetryPublish:
      latestAttempt?.outcome === 'failure' && !!publishIntegration?.available,
    canRetrySync:
      type.supportsNotionSync &&
      syncMeta.lastSyncStatus === 'failure' &&
      !!notionIntegration?.available,
    hasSyncFailure: syncMeta.lastSyncStatus === 'failure',
    hasPublishFailure: latestAttempt?.outcome === 'failure',
    hasIncompleteChecklist,
    publishChecklist,
    deliveryReceipt,
    deliveredViaLiveApi,
    performanceFeedback,
    latestPerformanceFeedback,
    hasPerformanceFeedback: hasPerformanceFeedbackCheckIn,
    deliveredManually:
      deliveryReceipt?.method === 'manual' ||
      deliveryReceipt?.method === 'export',
    scheduleState,
    lastError,
    syncMeta,
  }
}

export function deriveBriefTruthState({ brief, integrationHealth = null }) {
  const metadata =
    brief?.metadata && typeof brief.metadata === 'object' ? brief.metadata : {}
  const notionIntegration =
    integrationHealth?.integrations?.['notion-brief-sync'] || null
  const badges = [createBadge('saved', 'Saved', 'neutral')]

  if (brief?.notionBriefId) {
    badges.push(createBadge('synced', 'Synced to Notion', 'success'))
  } else if (metadata.lastSyncStatus === 'failure') {
    badges.push(createBadge('sync-failed', 'Sync failed', 'error'))
  } else if (!notionIntegration?.enabled || !notionIntegration?.available) {
    badges.push(createBadge('sync-unavailable', 'Sync unavailable', 'muted'))
  }

  return {
    badges,
    hasSyncFailure: metadata.lastSyncStatus === 'failure',
    lastError:
      metadata.lastSyncStatus === 'failure'
        ? {
            source: 'sync',
            code: metadata.lastSyncErrorCode || null,
            message: metadata.lastSyncErrorMessage || null,
            at: metadata.lastSyncAttemptAt || null,
          }
        : null,
  }
}

export function deriveReportTruthState(report) {
  const badges = [createBadge('saved', 'Saved', 'neutral')]
  if (report?.downloadUrl) {
    badges.push(createBadge('downloadable', 'Download ready', 'success'))
  }
  return { badges }
}

export default {
  getLatestPublishAttempt,
  getIntegrationForPublishingMode,
  isLivePublishingMode,
  getPublishReadiness,
  derivePostTruthState,
  deriveBriefTruthState,
  deriveReportTruthState,
}
